"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Fingerprint,
  LockKeyhole,
} from "lucide-react";
import { useAuth } from "@/lib/auth/provider";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { Brand, LangSwitcher } from "@/components/layout/shell";
import { Button, Empty, ErrorState, Loading } from "@/components/ui";
import type { Role } from "@/lib/types";
export function RequireAuth({
  children,
  roles,
}: {
  children: ReactNode;
  roles?: Role[];
}) {
  const auth = useAuth();
  const t = useT();
  if (auth.loading) return <Loading />;
  if (auth.error) return <ErrorState error={auth.error} retry={auth.retry} />;
  if (auth.mode === "keycloak" && !auth.configured)
    return (
      <Empty
        title={t("auth.not_configured_title")}
        text={t("auth.not_configured_text")}
        action={
          <Link href="/directions" className="button secondary">
            {t("nav.directions")}
          </Link>
        }
      />
    );
  if (!auth.authenticated)
    return (
      <Empty
        title={t("auth.login_required_title")}
        text={t("auth.login_required_text")}
        action={
          <Link href="/login" className="button primary">
            {t("auth.login_btn")}
          </Link>
        }
      />
    );
  if (!auth.user)
    return (
      <ErrorState
        error={t("auth.profile_error")}
        retry={auth.retry}
      />
    );
  if (roles && !roles.includes(auth.user.role))
    return (
      <Empty
        title={t("auth.no_access_title")}
        text={t("auth.no_access_text")}
        action={
          <Link href="/dashboard" className="button secondary">
            {t("common.home")}
          </Link>
        }
      />
    );
  return <>{children}</>;
}
function LocalCredentialsForm({
  mode,
}: {
  mode: "login" | "register" | "reset";
}) {
  const auth = useAuth();
  const t = useT();
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [googleReady, setGoogleReady] = useState<boolean | null>(null);
  const [googleNotice, setGoogleNotice] = useState(0);

  useEffect(() => {
    fetch("/api/backend/auth/google/status", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { enabled?: boolean } | null) =>
        setGoogleReady(d?.enabled ?? false),
      )
      .catch(() => setGoogleReady(false));
  }, []);

  function onGoogleClick() {
    if (googleReady) {
      // қосылған: 302 арқылы Google-ге апарылады
      window.location.href = "/api/backend/auth/google/login";
      return;
    }
    // қосылмаған: стильденген ескерту + shake-анимация
    setGoogleNotice((n) => n + 1);
  }

  if (mode === "reset")
    return (
      <div className="notice">{t("auth.reset_notice")}</div>
    );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    if (mode === "register" && password !== confirm) {
      setError("Пароли не совпадают.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register")
        await auth.localRegister(name, email, password);
      else await auth.localLogin(email, password);
      router.push("/dashboard");
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="task-form">
      {mode === "register" && (
        <label className="field">
          <span>{t("auth.name_label")}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            placeholder={t("auth.name_ph")}
          />
        </label>
      )}
      <label className="field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder="you@example.com"
        />
      </label>
      <label className="field">
        <span>Пароль</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          autoComplete={mode === "register" ? "new-password" : "current-password"}
          placeholder={
            mode === "register" ? t("auth.min8") : t("auth.your_password")
          }
        />
      </label>
      {mode === "register" && (
        <label className="field">
          <span>{t("auth.confirm_label")}</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" busy={busy}>
        {mode === "register" ? t("auth.register_btn") : t("auth.login_btn")}
        <ArrowRight size={17} />
      </Button>
      {mode === "login" && (
        <>
          <div className="auth-divider">
            <span>{t("auth.or")}</span>
          </div>
          {googleNotice > 0 && (
            <div
              key={googleNotice}
              className="google-notice"
              role="status"
            >
              <span className="google-notice-dot" />
              <span>{t("auth.google_soon")}</span>
            </div>
          )}
          <button
            type="button"
            className={`button google-btn ${googleReady ? "ready" : "pending"}`}
            onClick={onGoogleClick}
            title="Google"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" className="google-logo">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{t("auth.google_btn")}</span>
            {googleReady === false && (
              <span className="google-soon-badge">{t("auth.google_soon_badge")}</span>
            )}
          </button>
        </>
      )}
    </form>
  );
}
interface HeroStatsData {
  students: number;
  practices: number;
  courses: number;
  labs_running: number;
}

function HeroStats() {
  const t = useT();
  const req = useApi<HeroStatsData>("/public/stats");
  const d = req.data;
  const items = [
    { v: d?.students, k: "auth.stats_students" },
    { v: d?.practices, k: "auth.stats_practices" },
    { v: d?.courses, k: "auth.stats_courses" },
    { v: d?.labs_running, k: "auth.stats_labs_now" },
  ];
  return (
    <div
      className="hero-stats hero-rise"
      style={{ animationDelay: "1.25s" }}
      aria-label="Платформа статистикасы"
    >
      {items.map((i) => (
        <div key={i.k}>
          <b>{i.v != null ? i.v : "—"}</b>
          <span>{t(i.k)}</span>
        </div>
      ))}
    </div>
  );
}

export function AuthPage({ mode }: { mode: "login" | "register" | "reset" }) {
  const auth = useAuth();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const register = mode === "register";
  return (
    <div className="auth-page">
      <section className="auth-hero">
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-aurora hero-aurora-1" />
          <div className="hero-aurora hero-aurora-2" />
          <div className="hero-grid" />
          <div className="hero-radar" />
          <div className="hero-scan" />
        </div>
        <div className="hero-inner">
          <div className="hero-top">
            <Brand />
            <span className="hero-status">
              <i />
              {t("auth.hero_status")}
            </span>
          </div>
          <div className="hero-body">
            <div className="hero-eyebrow">{t("auth.hero_eyebrow")}</div>
            <h1 className="hero-title">
              {t("auth.story_h1a")}
              <br />
              {t("auth.story_h1b")}
              <br />
              <em>{t("auth.story_h1c")}</em>
            </h1>
            <p className="hero-mission hero-rise" style={{ animationDelay: ".9s" }}>
              {t("auth.hero_mission")}
            </p>
            <div className="hero-cta hero-rise" style={{ animationDelay: "1.1s" }}>
              <Link className="button primary hero-enter" href="/login">
                <span className="hero-enter-icon">
                  <ArrowRight size={18} />
                </span>
                {t("auth.cta_enter")}
              </Link>
              <Link className="button ghost hero-start" href="/register">
                {t("auth.cta_start")}
              </Link>
            </div>
            <HeroStats />
            <div className="hero-boot">
              <span className="hero-rise" style={{ animationDelay: "1.35s" }}>
                {t("auth.boot_1")}
              </span>
              <span className="hero-rise" style={{ animationDelay: "1.55s" }}>
                {t("auth.boot_2")}
              </span>
              <span className="hero-rise" style={{ animationDelay: "1.75s" }}>
                {t("auth.boot_3")}
              </span>
            </div>
          </div>
          <small className="hero-foot hero-rise" style={{ animationDelay: "1.95s" }}>
            {t("auth.directions_line")}
          </small>
        </div>
      </section>
      <section className="auth-form-side">
        <div className="inline" style={{ justifyContent: "space-between", marginBottom: 6 }}>
          <Link className="back-link" href="/dashboard">
            <ArrowLeft size={17} />
            {t("auth.back_home")}
          </Link>
          <LangSwitcher />
        </div>
        <div className="auth-form">
          <span className="auth-symbol">
            <Fingerprint size={32} />
          </span>
          <h2>
            {register
              ? t("auth.register_title")
              : mode === "reset"
                ? t("auth.reset_title")
                : t("auth.welcome_back")}
          </h2>
          <p>
            {register
              ? t("auth.register_sub")
              : mode === "reset"
                ? t("auth.reset_sub")
                : t("auth.login_sub")}
          </p>
          {auth.mode === "keycloak" && !auth.configured && (
            <div className="notice">
              Служба входа пока не подключена. Авторизация станет доступна после
              настройки платформы.
            </div>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {auth.error && <ErrorState error={auth.error} retry={auth.retry} />}
          {auth.mode === "local" ? (
            <LocalCredentialsForm mode={mode} />
          ) : (
            <Button
              busy={busy}
              disabled={!auth.configured || auth.loading}
              onClick={async () => {
                setBusy(true);
                setError(undefined);
                try {
                  await auth.login(register);
                } catch (e) {
                  setError((e as Error).message);
                  setBusy(false);
                }
              }}
            >
              {register
                ? "Перейти к регистрации"
                : mode === "reset"
                  ? "Открыть страницу восстановления"
                  : "Войти в аккаунт"}
              <ArrowRight size={17} />
            </Button>
          )}
          <div className="auth-security">
            <LockKeyhole size={15} />
            {auth.mode === "local"
              ? t("auth.security_local")
              : t("auth.security_keycloak")}
          </div>
          <div className="auth-switch">
            {register ? t("auth.already_have") : t("auth.no_account")}{" "}
            <Link href={register ? "/login" : "/register"}>
              {register ? t("auth.login_link") : t("auth.register_link")}
            </Link>
          </div>
          {mode === "login" && (
            <Link className="reset-link" href="/forgot-password">
              {t("auth.forgot_password")}
            </Link>
          )}
        </div>
        <small className="muted">{t("auth.learn_practice_protect")}</small>
      </section>
    </div>
  );
}
